/**
 * WebSocket client — consumes the orchestrator's /api/v1/ws gateway.
 *
 * Responsibilities:
 *   • one persistent connection, auto-reconnect with backoff on drops
 *   • subscription manager so multiple UI components watching the same
 *     profile reuse one server-side subscription
 *   • typed event API: `onProfilesChanged`, `onBackendChanged`,
 *     `onBackendLog`, `onTelegramChanged` — each returns an unsubscribe
 *     function
 *   • ping every 25s so proxies don't idle-kill the socket
 *
 * Does not validate incoming frames against the Zod schema — that happens
 * only at development-trust boundaries; callers receive typed payloads
 * via the shared TS types.
 */
import type {
	BackendLogEvent,
	BackendSupervisorState,
} from "@shared/api/schemas/backend";
import type { Mt5RuntimeStatusReport } from "@shared/api/schemas/mt5-runtime";
import type { DesktopProfilesSnapshot } from "@shared/api/schemas/profiles";
import type { TelegramSessionState } from "@shared/api/schemas/telegram";
import type { WsServerMessage } from "@shared/api/schemas/ws-events";
import { jwtStore } from "./jwt-store";

type Unsubscribe = () => void;

type GlobalTopic = "profiles";
type ProfileTopic = "backend" | "backendLog" | "telegram" | "mt5Runtime";

type ProfilesListener = (s: DesktopProfilesSnapshot) => void;
type BackendListener = (profileId: string, s: BackendSupervisorState) => void;
type BackendLogListener = (profileId: string, e: BackendLogEvent) => void;
type TelegramListener = (profileId: string, s: TelegramSessionState) => void;
type Mt5RuntimeListener = (
	profileId: string,
	s: Mt5RuntimeStatusReport,
) => void;
type ConnectedListener = () => void;

const RECONNECT_INITIAL_MS = 500;
const RECONNECT_MAX_MS = 15_000;
const PING_INTERVAL_MS = 25_000;

class WsClient {
	private ws: WebSocket | null = null;
	private reconnectMs = RECONNECT_INITIAL_MS;
	private pingTimer: ReturnType<typeof setInterval> | null = null;
	private shouldRun = false;

	// Subscription bookkeeping
	private readonly subscribedProfiles = new Set<string>();
	private readonly profilesListeners = new Set<ProfilesListener>();
	private readonly backendListeners = new Set<BackendListener>();
	private readonly backendLogListeners = new Set<BackendLogListener>();
	private readonly telegramListeners = new Set<TelegramListener>();
	private readonly mt5RuntimeListeners = new Set<Mt5RuntimeListener>();
	private readonly connectedListeners = new Set<ConnectedListener>();

	start(): void {
		if (this.shouldRun) return;
		this.shouldRun = true;
		this.connect();
	}

	stop(): void {
		this.shouldRun = false;
		this.subscribedProfiles.clear();
		this.profilesListeners.clear();
		this.backendListeners.clear();
		this.backendLogListeners.clear();
		this.telegramListeners.clear();
		this.mt5RuntimeListeners.clear();
		this.connectedListeners.clear();
		if (this.pingTimer) clearInterval(this.pingTimer);
		this.pingTimer = null;
		try {
			this.ws?.close(1000, "client stop");
		} catch {
			// ignore
		}
		this.ws = null;
	}

	/** Tell the server we're interested in a profile's scoped events. */
	subscribeProfile(profileId: string): Unsubscribe {
		this.subscribedProfiles.add(profileId);
		this.sendIfOpen({ type: "subscribe", profileId });
		return () => {
			this.subscribedProfiles.delete(profileId);
			this.sendIfOpen({ type: "unsubscribe", profileId });
		};
	}

	onProfilesChanged(cb: ProfilesListener): Unsubscribe {
		this.profilesListeners.add(cb);
		return () => {
			this.profilesListeners.delete(cb);
		};
	}
	onBackendChanged(cb: BackendListener): Unsubscribe {
		this.backendListeners.add(cb);
		return () => {
			this.backendListeners.delete(cb);
		};
	}
	onBackendLog(cb: BackendLogListener): Unsubscribe {
		this.backendLogListeners.add(cb);
		return () => {
			this.backendLogListeners.delete(cb);
		};
	}
	onTelegramChanged(cb: TelegramListener): Unsubscribe {
		this.telegramListeners.add(cb);
		return () => {
			this.telegramListeners.delete(cb);
		};
	}
	onMt5RuntimeChanged(cb: Mt5RuntimeListener): Unsubscribe {
		this.mt5RuntimeListeners.add(cb);
		return () => {
			this.mt5RuntimeListeners.delete(cb);
		};
	}
	/**
	 * Fires on every successful WS open — initial connection AND every
	 * reconnect after a drop. UI components hook this to re-fetch any
	 * REST state that may have drifted from the server's view while the
	 * socket was down (e.g. backend supervisor state after an orchestrator
	 * pod restart wipes the in-memory snapshot cache).
	 *
	 * Callback is invoked AFTER the gateway has had a chance to resend
	 * subscribe frames + replay cached state, so most listeners will be
	 * able to skip the re-fetch if the cache hit already updated them.
	 * That said, the right pattern is "always re-fetch; let it overwrite".
	 */
	onConnected(cb: ConnectedListener): Unsubscribe {
		this.connectedListeners.add(cb);
		return () => {
			this.connectedListeners.delete(cb);
		};
	}

	private connect(): void {
		if (!this.shouldRun) return;
		const session = jwtStore.get();
		if (!session) {
			// Can't connect without a token. Retry when user logs in.
			this.scheduleReconnect();
			return;
		}

		const base = import.meta.env.VITE_SIGNALLENS_API_BASE as
			| string
			| undefined;
		const resolvedBase = base ?? window.location.origin;
		const wsBase = resolvedBase.replace(/^http/, "ws");
		const url = `${wsBase}/api/v1/ws?token=${encodeURIComponent(session.token)}`;

		const ws = new WebSocket(url);
		this.ws = ws;

		ws.addEventListener("open", () => {
			this.reconnectMs = RECONNECT_INITIAL_MS;
			// Re-send subscribe for every profile we were watching before reconnect
			for (const profileId of this.subscribedProfiles) {
				ws.send(JSON.stringify({ type: "subscribe", profileId }));
			}
			if (this.pingTimer) clearInterval(this.pingTimer);
			this.pingTimer = setInterval(() => {
				try {
					ws.send(JSON.stringify({ type: "ping" }));
				} catch {
					// ignore
				}
			}, PING_INTERVAL_MS);
			// Notify consumers — they typically re-fetch any REST state
			// that might have drifted while the socket was down.
			for (const cb of this.connectedListeners) {
				try {
					cb();
				} catch {
					// ignore — one listener throwing shouldn't break others
				}
			}
		});

		ws.addEventListener("message", (ev) => {
			let parsed: WsServerMessage | null = null;
			try {
				parsed = JSON.parse(ev.data as string) as WsServerMessage;
			} catch {
				return;
			}
			this.dispatch(parsed);
		});

		ws.addEventListener("close", () => {
			if (this.pingTimer) clearInterval(this.pingTimer);
			this.pingTimer = null;
			this.ws = null;
			this.scheduleReconnect();
		});

		ws.addEventListener("error", () => {
			// `close` always follows — handle reconnect there.
		});
	}

	private scheduleReconnect(): void {
		if (!this.shouldRun) return;
		const delay = this.reconnectMs;
		this.reconnectMs = Math.min(this.reconnectMs * 2, RECONNECT_MAX_MS);
		setTimeout(() => this.connect(), delay);
	}

	private sendIfOpen(message: object): void {
		if (this.ws?.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify(message));
		}
	}

	private dispatch(msg: WsServerMessage): void {
		switch (msg.type) {
			case "pong":
				return;
			case "error":
				// The UI may show these via its own toast; for now, log.
				// biome-ignore lint/suspicious/noConsole: surfacing server errors
				console.warn("[ws] server error:", msg.message);
				return;
			case "profiles.stateChanged":
				for (const cb of this.profilesListeners) cb(msg.payload);
				return;
			case "backend.stateChanged":
				for (const cb of this.backendListeners) cb(msg.profileId, msg.payload);
				return;
			case "backend.logAppended":
				for (const cb of this.backendLogListeners)
					cb(msg.profileId, msg.payload);
				return;
			case "telegram.sessionStateChanged":
				for (const cb of this.telegramListeners) cb(msg.profileId, msg.payload);
				return;
			case "mt5Runtime.stateChanged":
				for (const cb of this.mt5RuntimeListeners)
					cb(msg.profileId, msg.payload);
				return;
		}
	}
}

export const wsClient = new WsClient();

// Expose topic discriminators as re-exports for callers that need them.
export type { GlobalTopic, ProfileTopic };
